import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Save, Settings2, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { payrollAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import PayrollPrimes from './PayrollPrimes';
import ComboImportDialog from '@/components/ComboImportDialog';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTH_LABELS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const FE_ALLOC_ORDER = ['FE Génipa', 'FE Cluny', 'FE Océanis', 'FE Dillon', 'FE Vauclin'];
const DEFAULT_ALLOCATION = { 'FE Génipa': 30, 'FE Cluny': 21, 'FE Océanis': 20.5, 'FE Dillon': 15.5, 'FE Vauclin': 13 };

const mondayOf = (d) => {
    const dt = new Date(d);
    dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
    return dt.toISOString().slice(0, 10);
};

const RatioBadge = ({ ratio, settings }) => {
    if (ratio == null) return <span className="text-slate-300">—</span>;
    const cls = ratio <= settings.target_ratio
        ? 'bg-emerald-100 text-emerald-700'
        : ratio <= settings.alert_ratio
            ? 'bg-amber-100 text-amber-700'
            : 'bg-red-100 text-red-700';
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums ${cls}`}>
            {ratio.toLocaleString('fr-FR')} %
        </span>
    );
};

export default function PayrollTab() {
    const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
    const [data, setData] = useState(null);
    const [edits, setEdits] = useState({}); // {`${siteId}|${date}`: 'raw string'}
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({ target_ratio: 30, alert_ratio: 35, commission_pct: 15, edouard_monthly: 0, edouard_allocation: DEFAULT_ALLOCATION });
    const [withEdouard, setWithEdouard] = useState(true);
    const [withPrimes, setWithPrimes] = useState(true);
    const [settingsDraft, setSettingsDraft] = useState(null);
    const [monthData, setMonthData] = useState(null);
    const [showComboImport, setShowComboImport] = useState(false);

    const loadWeek = useCallback(() => {
        payrollAPI.week(weekStart)
            .then(r => { setData(r.data); setEdits({}); })
            .catch(() => toast.error('Erreur de chargement de la semaine'));
    }, [weekStart]);

    const now = new Date();
    const [monthSel, setMonthSel] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });

    const loadMonth = useCallback(() => {
        payrollAPI.month(monthSel.year, monthSel.month).then(r => setMonthData(r.data)).catch(() => {});
    }, [monthSel]);

    useEffect(() => { loadWeek(); loadMonth(); }, [loadWeek, loadMonth]);
    useEffect(() => { payrollAPI.getSettings().then(r => setSettings(r.data)).catch(() => {}); }, []);

    const shiftMonth = (delta) => {
        setMonthSel(({ year, month }) => {
            const m = month + delta;
            if (m < 1) return { year: year - 1, month: 12 };
            if (m > 12) return { year: year + 1, month: 1 };
            return { year, month: m };
        });
    };

    const shiftWeek = (delta) => {
        const dt = new Date(weekStart);
        dt.setDate(dt.getDate() + delta * 7);
        setWeekStart(dt.toISOString().slice(0, 10));
    };

    const cellValue = (siteId, day) => {
        const k = `${siteId}|${day}`;
        if (k in edits) return edits[k];
        const v = data?.payroll?.[siteId]?.[day];
        return v == null ? '' : String(v);
    };

    const siteWeekTotals = useMemo(() => {
        if (!data) return {};
        const out = {};
        for (const s of data.sites) {
            let masse = 0;
            let ca = 0;
            for (const day of data.days) {
                masse += parseFloat(cellValue(s.id, day)) || 0;
                ca += data.ca?.[s.id]?.[day] || 0;
            }
            out[s.id] = { masse, ca, ratio: ca > 0 ? Math.round(masse / ca * 1000) / 10 : null };
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, edits]);

    const dirty = Object.keys(edits).length > 0;

    // Ratio effectif : masse saisie + primes (si activées) + quote-part Édouard (si activée) / CA net
    const effRatio = (masse, ca, edouard, primes) => {
        if (!(masse > 0) || !(ca > 0)) return null;
        const m = masse + (withPrimes ? (primes || 0) : 0) + (withEdouard ? (edouard || 0) : 0);
        return Math.round(m / ca * 1000) / 10;
    };
    const effMasse = (masse, edouard, primes) => masse + (withPrimes ? (primes || 0) : 0) + (withEdouard ? (edouard || 0) : 0);

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const entries = Object.entries(edits).map(([k, raw]) => {
                const [site_id, date] = k.split('|');
                const trimmed = String(raw).replace(',', '.').trim();
                return { site_id, date, amount: trimmed === '' ? null : (parseFloat(trimmed) || 0) };
            });
            await payrollAPI.saveWeek(entries);
            toast.success('Masse salariale enregistrée');
            loadWeek();
            loadMonth();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSettings = async () => {
        try {
            const r = await payrollAPI.saveSettings({
                target_ratio: parseFloat(settingsDraft.target_ratio) || 0,
                alert_ratio: parseFloat(settingsDraft.alert_ratio) || 0,
                commission_pct: parseFloat(settingsDraft.commission_pct) || 0,
                edouard_monthly: parseFloat(settingsDraft.edouard_monthly) || 0,
                edouard_allocation: Object.fromEntries(
                    FE_ALLOC_ORDER.map(n => [n, parseFloat(settingsDraft.edouard_allocation?.[n]) || 0])
                ),
            });
            setSettings(r.data);
            setSettingsDraft(null);
            toast.success('Réglages mis à jour');
            loadWeek();
            loadMonth();
        } catch {
            toast.error('Erreur lors de la mise à jour des seuils');
        }
    };

    if (!data) return <div className="text-sm text-slate-400 py-8 text-center">Chargement…</div>;

    const fmtDay = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
    const monthLabel = `${MONTH_LABELS[monthSel.month - 1]} ${monthSel.year}`;

    return (
        <div className="space-y-4" data-testid="payroll-tab">
            {/* Week navigation + actions */}
            <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="payroll-prev-week">
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="px-3 py-1.5 rounded-lg bg-slate-100 text-sm font-semibold" data-testid="payroll-week-label">
                    Semaine {data.week_number} — du {fmtDay(data.days[0])} au {fmtDay(data.days[6])}
                </div>
                <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="payroll-next-week">
                    <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))}>
                    Aujourd'hui
                </Button>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => setShowComboImport(true)} data-testid="payroll-combo-import-btn">
                    <Sparkles className="w-4 h-4 mr-1.5 text-emerald-500" />
                    Import Combo
                </Button>
                <Button size="sm" onClick={handleSave} disabled={!dirty || saving}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="payroll-save-btn">
                    <Save className="w-4 h-4 mr-1.5" />
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
            </div>

            {/* Weekly entry grid */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-500" />
                        Saisie hebdomadaire (montants journaliers Combo)
                    </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[860px]">
                        <thead>
                            <tr className="text-xs text-slate-500 border-b">
                                <th className="text-left py-2 pr-2">Site</th>
                                {data.days.map((day, i) => (
                                    <th key={day} className="text-center px-1 py-2 font-medium">
                                        {DAY_LABELS[i]}<br /><span className="font-normal text-slate-400">{fmtDay(day)}</span>
                                    </th>
                                ))}
                                <th className="text-right px-2 py-2">Total sem.</th>
                                <th className="text-right px-2 py-2">CA net sem.</th>
                                <th className="text-center px-2 py-2">Ratio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.sites.map(s => {
                                const t = siteWeekTotals[s.id] || { masse: 0, ca: 0, ratio: null };
                                return (
                                    <tr key={s.id} className="border-b last:border-0">
                                        <td className="py-1.5 pr-2 font-medium whitespace-nowrap">{s.name.replace('FE ', '')}</td>
                                        {data.days.map(day => (
                                            <td key={day} className="px-1 py-1.5 text-center">
                                                <Input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={cellValue(s.id, day)}
                                                    onFocus={e => e.target.select()}
                                                    onChange={e => setEdits(prev => ({ ...prev, [`${s.id}|${day}`]: e.target.value }))}
                                                    className="w-20 h-8 text-center text-xs tabular-nums"
                                                    placeholder="—"
                                                    data-testid={`payroll-input-${s.name.replace('FE ', '').toLowerCase()}-${day}`}
                                                />
                                            </td>
                                        ))}
                                        <td className="px-2 py-1.5 text-right font-semibold tabular-nums" data-testid={`payroll-week-total-${s.name.replace('FE ', '').toLowerCase()}`}>
                                            {t.masse > 0 ? formatCurrency(t.masse) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-2 py-1.5 text-right text-slate-500 tabular-nums">
                                            {t.ca > 0 ? formatCurrency(t.ca) : <span className="text-slate-300">—</span>}
                                        </td>
                                        <td className="px-2 py-1.5 text-center">
                                            <RatioBadge ratio={effRatio(t.masse, t.ca, data.edouard?.[s.id], data.primes?.[s.id])} settings={settings} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-2">
                        Le total semaine se calcule automatiquement — comparez-le au total hebdo affiché dans Combo pour contrôler votre saisie.
                        CA net = CA − {settings.commission_pct} % (commission Carrefour) ; les ratios sont calculés sur le CA net.
                        {withEdouard && settings.edouard_monthly > 0 && ' Le ratio « Avec Édouard » ajoute sa quote-part hebdomadaire répartie au prorata du CA annuel.'}
                    </p>
                </CardContent>
            </Card>

            {/* Monthly summary */}
            {monthData && (
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex flex-wrap items-center gap-2">
                            <CardTitle className="text-base">Ratio mensuel</CardTitle>
                            <div className="flex-1" />
                            <div className="inline-flex bg-slate-100 rounded-lg p-0.5 gap-0.5" data-testid="payroll-edouard-toggle">
                                <button
                                    type="button"
                                    onClick={() => setWithEdouard(false)}
                                    className={`px-3 h-7 rounded-md text-xs font-semibold ${!withEdouard ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                                    data-testid="payroll-toggle-sans-edouard"
                                >Équipe seule</button>
                                <button
                                    type="button"
                                    onClick={() => setWithEdouard(true)}
                                    className={`px-3 h-7 rounded-md text-xs font-semibold ${withEdouard ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                                    data-testid="payroll-toggle-avec-edouard"
                                >Avec Édouard</button>
                            </div>
                            <div className="inline-flex bg-slate-100 rounded-lg p-0.5 gap-0.5" data-testid="payroll-primes-toggle">
                                <button
                                    type="button"
                                    onClick={() => setWithPrimes(false)}
                                    className={`px-3 h-7 rounded-md text-xs font-semibold ${!withPrimes ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                                    data-testid="payroll-toggle-sans-primes"
                                >Sans primes</button>
                                <button
                                    type="button"
                                    onClick={() => setWithPrimes(true)}
                                    className={`px-3 h-7 rounded-md text-xs font-semibold ${withPrimes ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                                    data-testid="payroll-toggle-avec-primes"
                                >Avec primes</button>
                            </div>
                            <Popover open={!!settingsDraft} onOpenChange={(o) => setSettingsDraft(o ? { ...settings, edouard_allocation: { ...DEFAULT_ALLOCATION, ...(settings.edouard_allocation || {}) } } : null)}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-7" data-testid="payroll-settings-btn">
                                        <Settings2 className="w-4 h-4 mr-1.5" />
                                        Seuils : {settings.target_ratio}% / {settings.alert_ratio}% · Com. {settings.commission_pct}%
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 space-y-3">
                                    <p className="text-sm font-semibold">Réglages ratio masse / CA</p>
                                    <div className="space-y-1">
                                        <label className="text-xs text-slate-500">Cible (vert jusqu'à)</label>
                                        <Input type="number" step="0.5" value={settingsDraft?.target_ratio ?? ''}
                                            onChange={e => setSettingsDraft(d => ({ ...d, target_ratio: e.target.value }))}
                                            data-testid="payroll-target-input" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs text-slate-500">Alerte (rouge au-delà de)</label>
                                        <Input type="number" step="0.5" value={settingsDraft?.alert_ratio ?? ''}
                                            onChange={e => setSettingsDraft(d => ({ ...d, alert_ratio: e.target.value }))}
                                            data-testid="payroll-alert-input" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs text-slate-500">Commission Carrefour (% déduits du CA)</label>
                                        <Input type="number" step="0.5" value={settingsDraft?.commission_pct ?? ''}
                                            onChange={e => setSettingsDraft(d => ({ ...d, commission_pct: e.target.value }))}
                                            data-testid="payroll-commission-input" />
                                    </div>
                                    <div className="border-t pt-2 space-y-1">
                                        <label className="text-xs font-semibold text-slate-600">Salaire mensuel Édouard (€)</label>
                                        <Input type="number" step="50" value={settingsDraft?.edouard_monthly ?? ''}
                                            onChange={e => setSettingsDraft(d => ({ ...d, edouard_monthly: e.target.value }))}
                                            data-testid="payroll-edouard-monthly-input" />
                                        <p className="text-[10px] text-slate-400">Réparti au prorata du CA annuel des stands :</p>
                                        {FE_ALLOC_ORDER.map(name => (
                                            <div key={name} className="flex items-center gap-2">
                                                <span className="text-xs text-slate-500 w-16">{name.replace('FE ', '')}</span>
                                                <Input type="number" step="0.5" className="h-7 text-xs"
                                                    value={settingsDraft?.edouard_allocation?.[name] ?? ''}
                                                    onChange={e => setSettingsDraft(d => ({
                                                        ...d,
                                                        edouard_allocation: { ...d.edouard_allocation, [name]: e.target.value },
                                                    }))}
                                                    data-testid={`payroll-alloc-${name.replace('FE ', '').toLowerCase()}`} />
                                                <span className="text-xs text-slate-400">%</span>
                                            </div>
                                        ))}
                                        {(() => {
                                            const sum = FE_ALLOC_ORDER.reduce((s, n) => s + (parseFloat(settingsDraft?.edouard_allocation?.[n]) || 0), 0);
                                            return (
                                                <p className={`text-[10px] font-semibold ${Math.abs(sum - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                    Total : {sum.toLocaleString('fr-FR')} % {Math.abs(sum - 100) < 0.01 ? '✓' : '(doit faire 100 %)'}
                                                </p>
                                            );
                                        })()}
                                    </div>
                                    <Button size="sm" className="w-full" onClick={handleSaveSettings} data-testid="payroll-settings-save">
                                        Enregistrer
                                    </Button>
                                </PopoverContent>
                            </Popover>
                            <div className="flex items-center gap-1.5">
                                <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => shiftMonth(-1)} data-testid="payroll-prev-month">
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="px-2 py-1 rounded-md bg-slate-100 text-sm font-semibold min-w-[130px] text-center" data-testid="payroll-month-label">
                                    {monthLabel}
                                </span>
                                <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => shiftMonth(1)} data-testid="payroll-next-month">
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[560px]" data-testid="payroll-month-table">
                            <thead>
                                <tr className="text-xs text-slate-500 border-b">
                                    <th className="text-left py-2">Semaine</th>
                                    {monthData.sites.map(s => (
                                        <th key={s.id} className="text-center px-2 py-2">{s.name.replace('FE ', '')}</th>
                                    ))}
                                    <th className="text-center px-2 py-2 font-semibold">Total FE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthData.weeks.map(w => (
                                    <tr key={w.week} className="border-b">
                                        <td className="py-1.5 text-slate-600">{w.week} <span className="text-xs text-slate-400">({fmtDay(w.start)}–{fmtDay(w.end)})</span></td>
                                        {w.sites.map(sw => (
                                            <td key={sw.site_id} className="px-2 py-1.5 text-center">
                                                <RatioBadge ratio={effRatio(sw.masse, sw.ca, sw.edouard, sw.primes)} settings={settings} />
                                            </td>
                                        ))}
                                        <td className="px-2 py-1.5 text-center">
                                            <RatioBadge ratio={effRatio(w.total.masse, w.total.ca, w.total.edouard, w.total.primes)} settings={settings} />
                                        </td>
                                    </tr>
                                ))}
                                <tr className="bg-slate-50 font-semibold">
                                    <td className="py-2">Mois</td>
                                    {monthData.per_site.map(ps => (
                                        <td key={ps.site_id} className="px-2 py-2 text-center">
                                            <div className="flex flex-col items-center gap-0.5">
                                                <RatioBadge ratio={effRatio(ps.masse, ps.ca, ps.edouard, ps.primes)} settings={settings} />
                                                {ps.masse > 0 && (
                                                    <span className="text-[10px] text-slate-400 font-normal">
                                                        {formatCurrency(effMasse(ps.masse, ps.edouard, ps.primes))} / {formatCurrency(ps.ca)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    ))}
                                    <td className="px-2 py-2 text-center" data-testid="payroll-month-total">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <RatioBadge ratio={effRatio(monthData.total.masse, monthData.total.ca, monthData.total.edouard, monthData.total.primes)} settings={settings} />
                                            {monthData.total.masse > 0 && (
                                                <span className="text-[10px] text-slate-400 font-normal">
                                                    {formatCurrency(effMasse(monthData.total.masse, monthData.total.edouard, monthData.total.primes))} / {formatCurrency(monthData.total.ca)}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* Primes personnel (saisie mensuelle, duplicable) */}
            {data && (
                <PayrollPrimes
                    monthSel={monthSel}
                    sites={data.sites}
                    onChanged={() => { loadWeek(); loadMonth(); }}
                />
            )}

            <ComboImportDialog
                open={showComboImport}
                onClose={() => setShowComboImport(false)}
                onImported={() => { loadWeek(); loadMonth(); }}
            />
        </div>
    );
}
